import { ref } from 'vue'
import _ from 'lodash'
import type Building from '@/types/building'
import { useBuildingStore } from '@/store/building'
import { useUnitStore } from '@/store/unit'
import { hexToRgb } from '@/utils/colorHelpers'
import type Status from '@/types/status'

export function useBuildingChooser() {
    const status = ref('')
    const currentBuildingPublicId = ref(null)
    const currentBuilding = ref<Building | null>(null)

    const hoverBuildingPublicId = ref(null)
    const hoverBuilding = ref<Building | null>(null)

    const buildingStore = useBuildingStore()
    const unitStore = useUnitStore()

    const buildingStatistics = unitStore.getBuildingUnitsStatistics

    function colorCallback(buildingPublicId: string, state: string) {
        let color = null

        const building = buildingStore.buildings.find(
            (building) => building.publicId === buildingPublicId
        )

        if (!building) {
            return color
        }

        if (building && building.active) {
            const buildingStatistics = unitStore.getBuildingUnitsStatistics(building.id)

            const buildingIsCompletelySold =
                buildingStatistics.soldUnits === buildingStatistics.totalUnits

            const buildingUnitsStatusSummary = unitStore.getBuildingUnitsStatusSummary(building.id)

            let status: Status = null
            let index = 0

            while (index < buildingUnitsStatusSummary.length) {
                //get all stautses for this project and count of units under each status under this building
                const oneStatus = buildingUnitsStatusSummary[index].status

                // color overrides form embed
                let colorOverrides = {}
                status = {
                    ...oneStatus,
                    ...colorOverrides
                }

                index = index + 1

                if (status.isSold && buildingIsCompletelySold) {
                    break
                }

                // if this status isSold , use the next status
                if (!status.isSold) break
            }

            if (state === 'idle') {
                if (status.persistentBuildingColor) {
                    color = {
                        ...hexToRgb(status.idleColor),
                        s: status.idleStrokeWidth
                    }

                    return color
                }

                return color
            }

            if (state === 'hover' && hoverBuildingPublicId.value === buildingPublicId) {
                color = {
                    ...hexToRgb(status.highlightColor),
                    s: status.highlightStrokeWidth
                }
            }

            if (state === 'selected')
                if (state === 'selected' && currentBuildingPublicId.value === buildingPublicId) {
                    color = {
                        ...hexToRgb(status.activeColor),
                        s: status.activeStrokeWidth
                    }
                }
        }

        return color
    }

    const hoverOver = (buildingPublicId: string) => {
        status.value = 'hoverOver ' + buildingPublicId
        hoverBuildingPublicId.value = buildingPublicId
    }

    const hoverOut = (buildingPublicId: string) => {
        status.value = 'hoverOut ' + buildingPublicId
        hoverBuildingPublicId.value = null
    }

    const select = (buildingPublicId: string) => {
        status.value = 'select ' + buildingPublicId
        currentBuildingPublicId.value = buildingPublicId
    }

    const goTo = (buildingPublicId, projectSlug) => {
        select(buildingPublicId)
        status.value = 'goTo ' + buildingPublicId
        currentBuildingPublicId.value = buildingPublicId

        const building = buildingStore.buildings.find(
            (building) => building.publicId === buildingPublicId
        )

        if (building) navigateWithHash(`/${projectSlug}/${building.slug}/`)
    }

    const nav = (buildingPublicId: string) => {
        // TODO: nav logic here from embed
        status.value = 'nav ' + buildingPublicId
        currentBuildingPublicId.value = buildingPublicId
    }

    const showCards = computed(() => {
        // TODO: show card logic here from embed
        return true
    })

    const cdnFileResolver = (projectAssetRoot: string, filename: string) => {
        return `${projectAssetRoot}/buildings/images${filename}`
    }

    const canGoToBuilding = (buildingPublicId: string) => {
        const building = buildingStore.buildings.find(
            (building) => building.publicId === buildingPublicId
        )
        if (building)
            return building && building.active && buildingStatistics(building.id).availableUnits > 0
        return false
    }

    const buildingCallback = (buildingPublicId: string) => {
        const returnBuilding = buildingStore.buildings.find(
            (building) => building.publicId === buildingPublicId
        )
        return returnBuilding
    }

    watch(currentBuildingPublicId, (newBuildingPublicId: string, _) => {
        const building = buildingStore.buildings.find(
            (building) => building.publicId === newBuildingPublicId
        )

        if (building) currentBuilding.value = building
    })

    watch(hoverBuildingPublicId, (newBuildingPublicId: string, _) => {
        const building = buildingStore.buildings.find(
            (building) => building.publicId === newBuildingPublicId
        )

        if (building) hoverBuilding.value = building
    })

    return {
        cdnFileResolver,
        status,
        colorCallback,
        hoverOver,
        hoverOut,
        select,
        goTo,
        nav,
        canGoToBuilding,
        buildingCallback,
        showCards,
        currentBuildingPublicId,
        currentBuilding,
        hoverBuildingPublicId,
        hoverBuilding
    }
}
